import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { ThemeProvider, CssBaseline, Box, Typography, Divider, Button, Stack } from '@mui/material';
import { MainLayout, Section } from './layout';
import { Hero, Youtube } from './components';
import { BioPage } from './bio';
import { ConcertsPage } from './concerts';
import { ContactForm } from './contact';
import { SeoHelmet, HOME_SEO, PERSON_JSON_LD } from './seo';
import theme from './theme';

// Couleur / storytelling (île de Gorée) — pas de N&B dans cette section.
const UNIVERS_IMG = '/img/facebook/488658612_1610008269940580_7860712464783969435_n.jpg';
const YOUTUBE_TEASER_URL = 'https://www.youtube.com/shorts/tRTqttVqC9I';

function HomePage() {
  return (
    <>
      <SeoHelmet {...HOME_SEO} jsonLd={PERSON_JSON_LD} />
      <Hero
        title="Patrick NOAH"
        tagline="Créateur de l'AfroMusicS, Patrick Noah métisse les rythmes et mélodies issus d'Afrique et de Cuba. Sa musique est la synthèse entre le Jazz, l'Afro-Cubain et les Racines Africaines."
      />

      <Section
        sx={{
          backgroundImage: 'linear-gradient(to bottom, rgba(255, 51, 51, 0.08) 0%, rgba(26, 43, 72, 0.03) 100%)',
          pt: { xs: 4, md: 5 },
          pb: { xs: 4, md: 5 },
        }}
      >
        <Typography
          variant="overline"
          sx={{ display: 'block', mb: 1, color: 'primary.main', fontWeight: 700, letterSpacing: '0.12em' }}
        >
          Actualités
        </Typography>
        <Typography variant="h3" sx={{ mb: 1.25, fontWeight: 800, letterSpacing: '-0.02em' }}>
          « Isa Men » — nouveau single
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 980 }}>
          Sortie le 17 avril 2026 sur toutes les plateformes (clip officiel le même jour à 17h00). Une traversée afro-jazz
          qui s’embrase en bikutsi et timba cubana, en featuring avec la chanteuse cubaine Yairis Belet. L’album « Tecla »
          (11 titres, 7 langues) est porté par une énergie métissée et un projet né grâce au soutien de 109 contributeurs
          dans 16 pays.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 2.25, alignItems: { sm: 'center' } }}>
          <Button
            component="a"
            href={YOUTUBE_TEASER_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            color="primary"
          >
            Voir le teaser
          </Button>
        </Stack>
      </Section>

      <Divider sx={{ borderColor: 'primary.main', opacity: 0.2 }} />

      <Section
        sx={{
          // Fond doux (pas blanc), avec un dégradé très léger pour éviter l'effet "fade".
          backgroundImage: 'linear-gradient(to bottom, rgba(26, 43, 72, 0.06) 0%, rgba(26, 43, 72, 0.02) 100%)',
          // Important: ne pas écraser le padding vertical de Section.
          pt: { xs: 5, md: 7 },
          pb: { xs: 5, md: 7 },
        }}
      >
        {/* Grid : une seule ligne desktop → les deux colonnes ont la même hauteur (celle du texte). */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.22fr) minmax(0, 1fr)' },
            columnGap: { xs: 0, md: 5 },
            rowGap: 3,
            alignItems: 'stretch',
          }}
        >
          <Box sx={{ minWidth: 0, minHeight: 0 }}>
            <Typography variant="h2" color="primary" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mb: 3 }}>
              L'univers Patrick Noah
            </Typography>
            <Typography variant="body1" paragraph>
              Né à Yaoundé, Patrick Noah reçoit sa première guitare des mains de sa mère Técla à 11 ans et ne la quitte plus. De la chorale de quartier à une quête ancrée dans les racines, il forge un style qu'il nomme AfroMusicS : une fusion de jazz, d'afro-cubain et de racines africaines. Pour lui, c'est un objectif philosophique — donner à la musique africaine la dimension universelle qu'elle mérite et faire vivre l'héritage légué par les ancêtres, en Afrique, aux Caraïbes, en Amérique ou en Europe.
            </Typography>
            <Typography variant="body1" paragraph>
              Passionné par Cuba, il collabore avec des figures majeures : Septeto Araison, Orishas, Manolito Simonet, Bamboleo, Los Papines. Sur scène, guitares, percussions, cuivres et chœurs servent une énergie contagieuse qui invite au mouvement et à la danse. Chaque concert est un tissage de sonorités ethniques et modernes, chaleureux et exaltant.
            </Typography>
            <Typography variant="body1" paragraph>
              Son dernier album, distribué dans 22 pays, incarne une rencontre entre le Cameroun et Cuba — Patrick Noah et Ray Guerrero avec le Septeto Araison. Même quête : puiser à la source des musiques d'origine africaine pour offrir au public un héritage magnifique et universel.
            </Typography>
            <Typography variant="body1" paragraph>
              Son nouvel album TECLA (en hommage à sa mère Técla décédée en 2020, où une chanson lui est dédiée, le titre « Tecla ») a été enregistré à La Havane à Cuba et incarne une vision globale de la musique : métissée, engagée et vivante. L'album compte onze chansons et est chanté en sept langues. Dans cet album Patrick Noah vibre l'Afrique et transpire Cuba. Patrick Noah y chante la transmission, le métissage, le positivisme, l'engagement pour les droits des femmes et l'amour de l'autre. Patrick Noah a déjà commencé à distiller quelques singles issus de cet album, disponibles sur toutes les plateformes : ISA MEN (sortie le 17 avril 2026), LA MUSIQUE DE L'AFRIQUE (sortie le 14 mars 2025), MAMI NANGAÏ (sortie le 25 octobre 2024), MACUSA (sortie le 17 mai 2024) et DIS OUI (sortie le 19 janvier 2024), la plus populaire avec plus de 203 000 vues sur YouTube. L'artiste souhaite encore peut-être dévoiler un ou deux singles avant de sortir l'album entier avec ses 11 titres. Produit grâce au soutien de 109 contributeurs internautes issus de 16 pays via une opération de crowdfunding, l'album Tecla est une fièvre douce et puissante à la fois : celle qui donne envie de danser, de réfléchir, de rêver, de partager et de célébrer.
            </Typography>
          </Box>

          <Box
            sx={{
              minWidth: 0,
              minHeight: 0,
              alignSelf: 'stretch',
              display: 'flex',
              flexDirection: 'column',
              height: { xs: 'auto', md: '100%' },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                flex: { xs: 'none', md: 1 },
                minHeight: { xs: 280, md: 0 },
                height: { xs: 280, md: '100%' },
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 12px 40px rgba(26, 43, 72, 0.15)',
              }}
            >
              <Box
                component="img"
                src={UNIVERS_IMG}
                alt="Patrick Noah à Gorée, Maison des Esclaves"
                sx={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: { xs: 'center 40%', md: 'center 42%' },
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                }}
              />
            </Box>
          </Box>
        </Box>
      </Section>

      <Divider sx={{ borderColor: 'primary.main', opacity: 0.15 }} />

      <Section
        sx={{
          // Plus "joyeux" / lumineux que dark, sans passer en blanc agressif.
          backgroundImage:
            'linear-gradient(to bottom, rgba(26, 43, 72, 0.06) 0%, rgba(26, 43, 72, 0.02) 100%)',
        }}
      >
        <Youtube />
      </Section>
    </>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <MainLayout title="Patrick Noah">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/bio" element={<BioPage />} />
          <Route path="/concerts" element={<ConcertsPage />} />
          <Route path="/contact" element={<ContactForm />} />
        </Routes>
      </MainLayout>
      <Analytics />
    </ThemeProvider>
  );
}

export default App;
